// @ts-strict-check
import React, { useMemo, useRef, useState } from 'react';
import { LoadingSpinner, ErrorMessage, EmptyState, ToggleButton } from '../ui';
import ChartControls from '../ui/ChartControls';
import { BarChart } from '../charts';
import { zeroBaselinePlugin } from '../../utils/chartPlugins';
import { useMarketPulseData } from '../../hooks/useClubSections';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartColors, getChartJsOptions } from '../../utils/themeHelpers';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { TimePeriod } from '../../types/api';
import {
  METRICS,
  MetricKey,
  PulseRow,
  MARKET_PULSE_DASHBOARD,
  MARKET_PULSE_DASHBOARD_SOURCE,
  MARKET_PULSE_LABELS,
  MARKET_PULSE_TREND,
  MARKET_PULSE_TREND_ID,
} from './marketPulseConstants';
import { fmtPct } from './marketPulseFormatting';
import {
  computeMarketPulseComparisonData,
  MarketPulseComparisonData,
} from './marketPulseComparisonData';

interface MarketPulseTrendSectionProps {
  branchId?: number;
  customTimeRange?: TimePeriod;
  extrapolateToFullMonth?: boolean;
  comparisonData?: MarketPulseComparisonData;
  trendMetric?: MetricKey;
  className?: string;
}

const MarketPulseTrendSection: React.FC<MarketPulseTrendSectionProps> = ({
  branchId = 0,
  customTimeRange,
  extrapolateToFullMonth = false,
  comparisonData: externalComparisonData,
  trendMetric: initialTrendMetric = 'salesGrowth',
  className = '',
}) => {
  const contentRef = useRef<HTMLElement>(null);
  const { currentTheme } = useTheme();
  const chartColors = getChartColors(currentTheme);
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();

  const [trendMetric, setTrendMetric] = useState<MetricKey>(initialTrendMetric);

  const shouldFetch = !externalComparisonData;
  const { data, isLoading, error, refetch } = useMarketPulseData(
    shouldFetch ? branchId : undefined,
    customTimeRange,
  );

  const comparisonData = useMemo(() => {
    if (externalComparisonData) return externalComparisonData;
    if (!data) return null;
    const branchAll = (data.branchData as PulseRow[]) || [];
    const marketAll = (data.allRestaurantsData as PulseRow[]) || [];
    const marketPartialLatest = (data.allRestaurantsPartialLatestData as PulseRow[]) || [];
    const baseMonths = data.baseMonths ?? branchAll.length;
    return computeMarketPulseComparisonData(
      branchAll,
      marketAll,
      baseMonths,
      extrapolateToFullMonth,
      marketPartialLatest,
    );
  }, [externalComparisonData, data, extrapolateToFullMonth]);

  const trendChartData = useMemo(() => {
    if (!comparisonData) {
      return { labels: [] as string[], datasets: [] };
    }
    const labels = comparisonData.chartRows.map((r) => r.label);
    return {
      labels,
      datasets: [
        {
          label: MARKET_PULSE_LABELS.branch,
          data: comparisonData.chartRows.map((r) => r.cells[trendMetric].branch),
          backgroundColor: chartColors[0],
          borderColor: chartColors[0],
          borderWidth: 1,
          borderRadius: 6,
          borderSkipped: false,
          maxBarThickness: 36,
        },
        {
          label: MARKET_PULSE_LABELS.market,
          data: comparisonData.chartRows.map((r) => r.cells[trendMetric].market),
          backgroundColor: chartColors[1] || '#94a3b8',
          borderColor: chartColors[1] || '#94a3b8',
          borderWidth: 1,
          borderRadius: 6,
          borderSkipped: false,
          maxBarThickness: 36,
        },
      ],
    };
  }, [comparisonData, trendMetric, chartColors]);

  const trendChartOptions = useMemo(() => {
    const base = getChartJsOptions(currentTheme) as Record<string, unknown>;
    const axisPct = (value: unknown): string => {
      const n = typeof value === 'number' ? value : Number(value);
      return Number.isNaN(n) ? '' : `${n.toFixed(2)}%`;
    };
    const baseScales = (base.scales ?? {}) as Record<string, unknown>;
    const baseY = (baseScales.y ?? {}) as Record<string, unknown>;
    const baseYTicks = (baseY.ticks ?? {}) as Record<string, unknown>;
    const basePlugins = (base.plugins ?? {}) as Record<string, unknown>;
    const baseTooltip = (basePlugins.tooltip ?? {}) as Record<string, unknown>;
    const baseTooltipCallbacks = (baseTooltip.callbacks ?? {}) as Record<string, unknown>;

    return {
      ...base,
      scales: {
        ...baseScales,
        y: {
          ...baseY,
          ticks: { ...baseYTicks, callback: axisPct },
        },
      },
      plugins: {
        ...basePlugins,
        tooltip: {
          ...baseTooltip,
          callbacks: {
            ...baseTooltipCallbacks,
            label: (ctx: { dataset?: { label?: string }; parsed?: { y?: number | null } }) =>
              `${ctx.dataset?.label ?? ''}: ⁦${fmtPct(ctx.parsed?.y ?? null)}⁩`,
          },
        },
      },
    };
  }, [currentTheme]);

  if (shouldFetch && isLoading) {
    return (
      <div className={`card-base card-padding flex justify-center py-12 ${className}`}>
        <LoadingSpinner />
      </div>
    );
  }

  if (shouldFetch && error) {
    return (
      <div className={`card-base card-padding ${className}`}>
        <ErrorMessage message={MARKET_PULSE_DASHBOARD.errorFetch} onRetry={() => refetch()} />
      </div>
    );
  }

  if (!comparisonData || comparisonData.chartRows.length === 0) {
    return (
      <div className={`card-base card-padding ${className}`}>
        <EmptyState message={MARKET_PULSE_DASHBOARD.emptyNoData} />
      </div>
    );
  }

  return (
    <section ref={contentRef} className={`card-base card-padding space-y-6 ${className}`}>
      <ChartControls
        componentId={MARKET_PULSE_TREND_ID}
        componentTitle={MARKET_PULSE_TREND.title}
        componentType="chart"
        dashboardSource={MARKET_PULSE_DASHBOARD_SOURCE}
        relatedLinks={[...MARKET_PULSE_DASHBOARD.relatedLinks]}
        relatedLinksTitle={MARKET_PULSE_DASHBOARD.relatedLinksTitle}
        onInfo={() => openGuide(CHART_GUIDES.marketPulseTrend)}
        onFavorite={() => handleFavoriteToggle({
          componentId: MARKET_PULSE_TREND_ID,
          componentTitle: MARKET_PULSE_TREND.title,
          dashboardSource: MARKET_PULSE_DASHBOARD_SOURCE,
          componentType: 'chart',
          description: MARKET_PULSE_TREND.favoriteDescription,
          props: { branchId, trendMetric, extrapolateToFullMonth },
          gridLayout: { containerClass: 'grid grid-cols-1 gap-6' },
        })}
        isFavorited={isFavorited(MARKET_PULSE_TREND_ID)}
        chartRef={contentRef}
        className="mb-2"
      />

      <div className="flex flex-col items-center gap-6">
        <div className="text-center">
          <h3 className="title-card !mb-1 !text-center">{MARKET_PULSE_TREND.title}</h3>
          <p className="text-xs text-text-secondary">{MARKET_PULSE_TREND.subtitle}</p>
          {comparisonData.extrapolate && comparisonData.hasExtrapolatedMonths && (
            <p className="text-[11px] text-text-tertiary mt-1 max-w-md mx-auto leading-relaxed">
              {MARKET_PULSE_TREND.extrapolatedNote}
            </p>
          )}
        </div>
        <ToggleButton
          className="flex-wrap justify-center !mb-2"
          activeValue={trendMetric}
          onChange={(v) => setTrendMetric(v as MetricKey)}
          options={METRICS.map((m) => ({ value: m.key, label: m.shortLabel }))}
        />
      </div>

      <BarChart data={trendChartData} options={trendChartOptions} height={340} plugins={[zeroBaselinePlugin]} />

      <p className="text-xs text-text-secondary text-center -mt-2">
        {MARKET_PULSE_TREND.zeroLineNote}
      </p>

    </section>
  );
};

export default MarketPulseTrendSection;
